import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranscript } from '../transcripts/queries';
import { excerptKeys } from './queries';

/**
 * Hook to find an excerpt by its position within a codeable item.
 * Returns the excerpt with the most codings if multiple matches exist.
 *
 * @param {number} start - Start position
 * @param {number} end - End position
 * @param {number} codeableId - Codeable item ID (transcript or survey answer)
 * @param {string} codeableType - Type of codeable item ('Transcript' or 'SurveyAnswer')
 * @returns {Object|null} The matching excerpt with most codings, or null if none found
 */
export function useExcerptWithStartAndEnd(start, end, codeableId, codeableType) {
  const queryClient = useQueryClient();

  // Plain imperative reads, deliberately NOT the reactive observer hook:
  // codeableId flips null→id→null with every selection gesture, so a
  // useTranscriptExcerpts here constructed and tore down a full
  // per-excerpt observer set per highlight. Detail entries are
  // session-persistent (gcTime Infinity in queryClientConfig) and seeded
  // before their ids enter membership, so a member with no entry is an
  // invariant violation (reported by the page's resolution hooks), not a
  // state to wait out; membership changes (create/delete/refetch) re-run
  // the memo through transcriptData identity.
  const { data: transcriptData } = useTranscript(
    codeableType === 'Transcript' ? codeableId : null
  );

  const excerpt = useMemo(() => {
    if (start == null || end == null || !codeableId || !codeableType) {
      return null;
    }

    let excerpts = [];

    if (codeableType === 'Transcript' && Array.isArray(transcriptData?.excerpt_ids)) {
      excerpts = transcriptData.excerpt_ids
        .map((id) => queryClient.getQueryData(excerptKeys.detail(id)))
        .filter(Boolean);
    } else if (codeableType === 'SurveyAnswer') {
      // KNOWN GAP (pre-existing, documented on the PR): this branch scans
      // whatever detail entries happen to be cached, with no membership
      // list to check for missing entries — so a span selected before the
      // survey-excerpts query settles can read as a false miss, and the
      // create path then mints a duplicate excerpt over the same span
      // (excerpts#code has no span dedupe). A fix needs "distrust a miss
      // while that answer's survey-excerpts query is unsettled".
      // For SurveyAnswers, we need to look through cached excerpts
      // Since excerpts are cached individually when loaded from survey responses/questions,
      // we'll iterate through the excerpt cache
      const cache = queryClient.getQueryCache();
      const queries = cache.findAll({
        queryKey: excerptKeys.details(),
        exact: false
      });

      // Extract excerpt data from cached queries
      queries.forEach(query => {
        const excerptData = query.state?.data;
        if (excerptData &&
            excerptData.codeable_type === 'SurveyAnswer' &&
            excerptData.transcript_id === codeableId) {
          excerpts.push(excerptData);
        }
      });
    }

    // Find excerpts matching the position
    let bestMatch = null;

    excerpts.forEach(excerpt => {
      if (excerpt.start === start &&
          excerpt.end === end &&
          excerpt.transcript_id === codeableId &&
          excerpt.codeable_type === codeableType) {

        // If we don't have a match yet, or this one has more codings, use it
        if (!bestMatch ||
            (excerpt.codings && excerpt.codings.length > (bestMatch.codings?.length || 0))) {
          bestMatch = excerpt;
        }
      }
    });

    return bestMatch;
  }, [start, end, codeableId, codeableType, transcriptData, queryClient]);

  return excerpt;
}
